import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Heart, Star, Leaf, Eye, ShoppingCart, Loader2 } from 'lucide-react';
import { ROUTES } from '../../lib/routes';

interface ProductCardProps {
  id: string | number;
  name: string;
  price: number;
  image: string;
  isLatest?: boolean;
  origin?: string;
  rating?: number;
  reviewCount?: number;
  category?: string;
  isOrganic?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  id,
  name,
  price,
  image,
  isLatest = false,
  origin,
  rating,
  reviewCount,
  category,
  isOrganic = true,
}) => {
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const imageUrl = image?.startsWith('http') 
    ? image 
    : image || "https://images.pexels.com/photos/1306548/pexels-photo-1306548.jpeg";

  const displayRating = rating || 4.9;
  const displayReviewCount = reviewCount || 0;
  const locationText = origin || 'Nepal';

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsAddingToCart(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('Added to cart:', name);
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const productSlug = typeof id === 'string' ? id : String(id);
  
  return (
    <Link
      to={`${ROUTES.PRODUCTS}/${productSlug}`}
      className="group block bg-white rounded-[16px] overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] shadow-[0_2px_16px_rgba(0,0,0,0.08)] border border-[#EAEAEA]"
    >
      {/* Image Section */}
      <div className="relative h-[180px] sm:h-[220px] overflow-hidden bg-gray-100 cursor-pointer">
        <img
          src={imageUrl}
          alt={name}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 group-active:scale-105 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
        />
        
        {/* Loading placeholder */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />
        )}

        {/* Gradient overlay - appears on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* View Details Button - appears on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 z-[5]">
          <div className="bg-white/95 backdrop-blur-sm rounded-full px-6 py-3 flex items-center space-x-2 shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 pointer-events-none">
            <Eye className="w-4 h-4" style={{ color: '#2E7D32' }} />
            <span className="text-sm font-semibold" style={{ color: '#2E7D32' }}>View Details</span>
          </div>
        </div>

        {/* Heart/Wishlist Button (Top Right) */}
        <button
          onClick={handleWishlistClick}
          className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-all duration-200 hover:scale-110"
          aria-label="Add to wishlist"
        >
          <Heart
            className="w-5 h-5 transition-all duration-200"
            style={{
              fill: isWishlisted ? '#2E7D32' : 'none',
              color: isWishlisted ? '#2E7D32' : '#374151'
            }}
          />
        </button>

        {/* Organic Verified Badge (Top Left) */}
        {isOrganic && (
          <div className="absolute top-4 left-4 z-20">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full shadow-md">
              <Leaf className="w-3.5 h-3.5" style={{ color: '#2E7D32' }} />
              <span className="text-xs font-semibold" style={{ color: '#2E7D32' }}>Organic</span>
            </div>
          </div>
        )}

        {/* New Badge (if latest) */}
        {isLatest && (
          <div className="absolute top-4 left-4 z-20">
            <div className="inline-flex items-center px-3 py-1.5 text-white rounded-full shadow-md" style={{ backgroundColor: '#2E7D32' }}>
              <span className="text-xs font-semibold">✨ New</span>
            </div>
          </div>
        )}

        {/* Category Tag (Bottom Left) */}
        {category && (
          <div className="absolute bottom-4 left-4 z-20">
            <div className="inline-flex items-center px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full shadow-md">
              <span className="text-xs font-medium text-gray-700">{category}</span>
            </div>
          </div>
        )}
      </div>

      {/* Product Info Section */}
      <div className="p-3 sm:p-4 space-y-2">
        {/* Title */}
        <h3
          className="text-sm sm:text-base font-semibold text-[#1A1A1A] font-poppins line-clamp-2 transition-colors duration-200 min-h-[40px]"
        >
          {name}
        </h3>

        {/* Location */}
        <div className="flex items-center gap-1 text-[#717171] text-xs font-inter">
          <MapPin className="w-3 h-3 flex-shrink-0" />
          <span className="line-clamp-1">From {locationText}</span>
        </div>

        {/* Rating (if available) */}
        {(displayRating > 0 || displayReviewCount > 0) && (
          <div className="flex items-center gap-1 text-[#1A1A1A]">
            <Star className="w-3 h-3" style={{ fill: '#2E7D32', color: '#2E7D32' }} />
            <span className="text-xs font-medium font-inter">
              {displayRating.toFixed(1)}
            </span>
            {displayReviewCount > 0 && (
              <span className="text-xs text-[#717171] font-inter">
                ({displayReviewCount})
              </span>
            )}
          </div>
        )}

        {/* Price and Add to Cart Row */}
        <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-gray-100">
          <div className="flex-shrink-0">
            <div className="text-lg sm:text-xl font-bold font-poppins" style={{ color: '#2E7D32' }}>
              Rs {price.toFixed(2)}
            </div>
          </div>
          <button
            onClick={handleAddToCart}
            disabled={isAddingToCart}
            className={`flex items-center gap-1.5 px-3 py-2 text-white rounded-lg transition-all duration-200 font-medium text-xs shadow-sm ${
              isAddingToCart
                ? 'opacity-70 cursor-not-allowed'
                : 'hover:shadow-md active:scale-95'
            }`}
            style={{ backgroundColor: '#2E7D32' }}
            aria-label="Add to cart"
          >
            {isAddingToCart ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="hidden sm:inline">Adding...</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Add</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Link>
  );
};

